import React, { useState } from 'react';
import { PlusIcon, SearchIcon, EditIcon, TrashIcon, ShieldCheckIcon, LockIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Textarea } from '../ui/Textarea';
import { Modal } from '../ui/Modal';
import { Badge } from '../ui/Badge';
import { useData } from '../../contexts/DataContext';
import { Role } from '../../types';
export function Roles() {
  const {
    roles,
    addRole,
    updateRole,
    deleteRole
  } = useData();
  const [searchTerm, setSearchTerm] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    displayName: '',
    description: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const filteredRoles = roles.filter(role => role.displayName.toLowerCase().includes(searchTerm.toLowerCase()) || role.name.toLowerCase().includes(searchTerm.toLowerCase()) || role.description?.toLowerCase().includes(searchTerm.toLowerCase()));
  const openNewForm = () => {
    setSelectedRole(null);
    setFormData({
      name: '',
      displayName: '',
      description: ''
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openEditForm = (role: Role) => {
    if (role.isSystemRole) return; // Cannot edit system roles
    setSelectedRole(role);
    setFormData({
      name: role.name,
      displayName: role.displayName,
      description: role.description || ''
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openDeleteConfirm = (role: Role) => {
    if (role.isSystemRole) return; // Cannot delete system roles
    setSelectedRole(role);
    setIsDeleteConfirmOpen(true);
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Validation
    const errors: Record<string, string> = {};
    if (!formData.name.trim()) errors.name = 'El nombre interno es requerido';
    if (!formData.displayName.trim()) errors.displayName = 'El nombre visible es requerido';
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    try {
      if (selectedRole) {
        await updateRole(selectedRole.id, {
          displayName: formData.displayName,
          description: formData.description
        });
      } else {
        await addRole({
          name: formData.name.toLowerCase().replace(/\s+/g, '_'),
          displayName: formData.displayName,
          description: formData.description,
          isSystemRole: false
        });
      }
      setIsFormOpen(false);
    } catch (error) {
      console.error('Error saving role:', error);
      setFormErrors({
        submit: 'Error al guardar el rol'
      });
    }
  };
  const handleDelete = async () => {
    if (!selectedRole) return;
    try {
      await deleteRole(selectedRole.id);
      setIsDeleteConfirmOpen(false);
    } catch (error) {
      console.error('Error deleting role:', error);
    }
  };
  return <div className="space-y-6" data-id="element-1430">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-1431">
        <div data-id="element-1432">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2" data-id="element-1433">
            <ShieldCheckIcon className="w-6 h-6 text-sky-600 dark:text-sky-400" data-id="element-1434" />
            Roles
          </h1>
          <p className="text-gray-500 dark:text-gray-400" data-id="element-1435">
            Gestión de roles del sistema
          </p>
        </div>
        <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-1437" />} onClick={openNewForm} data-id="element-1436">
          Nuevo Rol
        </Button>
      </div>

      {/* Search */}
      <Card data-id="element-1438">
        <div className="flex items-center gap-4" data-id="element-1439">
          <div className="flex-1 relative" data-id="element-1440">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-1441" />
            <Input placeholder="Buscar roles..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" data-id="element-1442" />
          </div>
        </div>
      </Card>

      {/* Roles Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1443">
        {filteredRoles.length === 0 ? <Card className="col-span-full" data-id="element-1444">
            <div className="py-12 text-center text-gray-500 dark:text-gray-400" data-id="element-1445">
              No se encontraron roles
            </div>
          </Card> : filteredRoles.map(role => <Card key={role.id} className="hover:shadow-lg transition-shadow" data-id="element-1446">
              <div className="p-6" data-id="element-1447">
                <div className="flex items-start justify-between mb-4" data-id="element-1448">
                  <div className="flex items-center gap-3" data-id="element-1449">
                    <div className="w-12 h-12 rounded-lg bg-sky-100 dark:bg-sky-900/30 flex items-center justify-center" data-id="element-1450">
                      <ShieldCheckIcon className="w-6 h-6 text-sky-600 dark:text-sky-400" data-id="element-1451" />
                    </div>
                    <div data-id="element-1452">
                      <h3 className="font-semibold text-gray-900 dark:text-white" data-id="element-1453">
                        {role.displayName}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1454">
                        {role.name}
                      </p>
                    </div>
                  </div>
                  {role.isSystemRole && <Badge variant="info" data-id="element-1455">
                      <LockIcon className="w-3 h-3 mr-1" data-id="element-1456" />
                      Sistema
                    </Badge>}
                </div>

                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 min-h-[40px]" data-id="element-1457">
                  {role.description || 'Sin descripción'}
                </p>

                <div className="flex gap-2 pt-4 border-t border-gray-200 dark:border-gray-700" data-id="element-1458">
                  {role.isSystemRole ? <div className="flex-1 text-center text-sm text-gray-400 dark:text-gray-500 py-2" data-id="element-1459">
                      Rol del sistema (no editable)
                    </div> : <>
                      <Button variant="outline" size="sm" leftIcon={<EditIcon className="w-4 h-4" data-id="element-1461" />} onClick={() => openEditForm(role)} className="flex-1" data-id="element-1460">
                        Editar
                      </Button>
                      <Button variant="danger" size="sm" leftIcon={<TrashIcon className="w-4 h-4" data-id="element-1463" />} onClick={() => openDeleteConfirm(role)} className="flex-1" data-id="element-1462">
                        Eliminar
                      </Button>
                    </>}
                </div>
              </div>
            </Card>)}
      </div>

      {/* Role Form Modal */}
      <Modal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} title={selectedRole ? 'Editar Rol' : 'Nuevo Rol'} size="md" data-id="element-1464">
        <form onSubmit={handleSubmit} className="space-y-4" data-id="element-1465">
          {!selectedRole && <Input label="Nombre Interno (único)" value={formData.name} onChange={e => setFormData({
          ...formData,
          name: e.target.value
        })} error={formErrors.name} placeholder="ej: gerente_ventas" helperText="Solo letras minúsculas, números y guiones bajos" data-id="element-1466" />}

          <Input label="Nombre Visible" value={formData.displayName} onChange={e => setFormData({
          ...formData,
          displayName: e.target.value
        })} error={formErrors.displayName} placeholder="ej: Gerente de Ventas" data-id="element-1467" />

          <Textarea label="Descripción" value={formData.description} onChange={e => setFormData({
          ...formData,
          description: e.target.value
        })} rows={3} placeholder="Describe las responsabilidades de este rol..." data-id="element-1468" />

          {formErrors.submit && <div className="text-sm text-red-600 dark:text-red-400" data-id="element-1469">
              {formErrors.submit}
            </div>}

          <div className="flex justify-end gap-3 pt-4" data-id="element-1470">
            <Button variant="outline" type="button" onClick={() => setIsFormOpen(false)} data-id="element-1471">
              Cancelar
            </Button>
            <Button type="submit" data-id="element-1472">
              {selectedRole ? 'Guardar Cambios' : 'Crear Rol'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteConfirmOpen} onClose={() => setIsDeleteConfirmOpen(false)} title="Confirmar Eliminación" size="sm" data-id="element-1473">
        <div className="space-y-4" data-id="element-1474">
          <p className="text-gray-600 dark:text-gray-400" data-id="element-1475">
            ¿Está seguro de que desea eliminar el rol "
            {selectedRole?.displayName}"? Esta acción no se puede deshacer y
            también eliminará todos los permisos asociados.
          </p>
          <div className="flex justify-end gap-3" data-id="element-1476">
            <Button variant="outline" onClick={() => setIsDeleteConfirmOpen(false)} data-id="element-1477">
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleDelete} data-id="element-1478">
              Eliminar
            </Button>
          </div>
        </div>
      </Modal>
    </div>;
}